<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core';

const props = defineProps({
  activeTab: { type: String, default: 'main' },
  folders: { type: Array, default: () => [] },
  card: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['tabChange']);

function calendarAlert(card) {
  const ts = card?.next_meeting?.due_date;
  if (!ts) return null;
  const meetingDate = new Date(ts * 1000);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  meetingDate.setHours(0, 0, 0, 0);
  if (meetingDate < today) return null;
  if (meetingDate.getTime() === today.getTime()) return 'today';
  return 'future';
}

const { t } = useI18n();

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller('md');

const fixedTabs = [
  {
    id: 'main',
    icon: 'i-lucide-layout-dashboard',
    label: t('KANBAN.OPPORTUNITY_MODAL.NAV_MAIN'),
  },
  {
    id: 'tasks',
    icon: 'i-lucide-check-square',
    label: t('KANBAN.OPPORTUNITY_MODAL.NAV_TASKS'),
  },
  {
    id: 'calendar',
    icon: 'i-lucide-calendar',
    label: t('KANBAN.OPPORTUNITY_MODAL.NAV_CALENDAR'),
  },
  {
    id: 'notes',
    icon: 'i-lucide-file-text',
    label: t('KANBAN.OPPORTUNITY_MODAL.NAV_NOTES'),
  },
  {
    id: 'utm',
    icon: 'i-lucide-link',
    label: t('KANBAN.OPPORTUNITY_MODAL.NAV_UTM'),
  },
];

const historyTab = {
  id: 'history',
  icon: 'i-lucide-history',
  label: t('KANBAN.OPPORTUNITY_MODAL.NAV_HISTORY'),
};

const folderTabs = computed(() =>
  props.folders
    .filter(f => f.slug !== 'utm')
    .map(f => ({
      id: `folder-${f.id}`,
      icon: f.icon,
      label: f.name,
      slug: f.slug,
    }))
);

const allTabs = computed(() => [...fixedTabs, ...folderTabs.value, historyTab]);

function select(tabId) {
  emit('tabChange', tabId);
}
</script>

<template>
  <div
    class="flex flex-row md:flex-col items-center gap-1 py-1 px-2 md:py-3 md:px-1 border-b md:border-b-0 md:border-r border-n-weak w-full md:w-12 flex-shrink-0 bg-n-surface-2 overflow-x-auto md:overflow-visible"
  >
    <button
      v-for="tab in allTabs"
      :key="tab.id"
      v-tooltip="{
        content: tab.label,
        placement: isMobile ? 'bottom' : 'right',
      }"
      class="relative w-[38px] h-[38px] flex-shrink-0 flex items-center justify-center rounded transition-colors"
      :class="
        activeTab === tab.id
          ? 'bg-n-brand text-white'
          : 'text-n-slate-9 hover:bg-n-alpha-1 hover:text-n-slate-12'
      "
      @click="select(tab.id)"
    >
      <span class="size-6" :class="[tab.icon]" />
      <span
        v-if="tab.id === 'tasks' && card.task_alert"
        class="absolute top-1 right-1 w-2 h-2 rounded-full border border-white dark:border-n-surface-2"
        :class="
          card.task_alert === 'overdue'
            ? 'bg-n-ruby-9'
            : card.task_alert === 'due_today'
              ? 'bg-n-teal-9'
              : 'bg-n-amber-9'
        "
      />
      <span
        v-if="tab.id === 'calendar' && calendarAlert(card)"
        class="absolute top-1 right-1 w-2 h-2 rounded-full border border-white dark:border-n-surface-2"
        :class="calendarAlert(card) === 'today' ? 'bg-n-teal-9' : 'bg-n-iris-9'"
      />
    </button>
  </div>
</template>
